import React, { useCallback, useState, useMemo } from 'react';
import firebase from 'firebase/app';
import { memoNamed } from '../utils/react/memo-named';
import { User } from '../types/user';
import styled from 'styled-components';
import { NamesPrivilegeAdd } from './NamesPrivilegeAdd';
import { NamesPrivilegeInfo } from './NamesPrivilegeInfo';

const Table = styled.table`
  width: 100%;
  text-align: left;
`;

const Message = styled.div`
  color: red;
`;

const H4 = styled.h4`
  margin-bottom: 0;
`;

const Info = styled.div`
  padding: 5px;
  border-radius: 5px;
  background-color: #d3cbc6;
`;

const Right = styled.div`
  text-align: right;
`;

const Hr = styled.hr`
  margin-top: 0;
`;

const H3 = styled.h3`
  margin-top: 0;
`;

interface Props {
  docID: string;
  user: User;
  updateUserList: () => void;
}

export const NamesPrivilege = memoNamed<Props>('NamesPrivilege', (props) => {
  const [userPrivilegePeriod, setUserPrivilegePeriod] = useState<
    { start: firebase.firestore.Timestamp; end: firebase.firestore.Timestamp }[]
  >(props.user.privilegePeriod);
  const [message, setMessage] = useState<string>('');
  const [isLoadingUpdate, setIsLoadingUpdate] = useState<boolean>(false);
  const [isLoadingRemove, setIsLoadingRemove] = useState<boolean>(false);

  const userPrivilegePeriodSorted = useMemo(
    () =>
      userPrivilegePeriod.sort((a, b) => b.end.toMillis() - a.end.toMillis()), // descending order of date
    [userPrivilegePeriod]
  );

  const deletePrivilege = useCallback(
    (
      start: firebase.firestore.Timestamp,
      end: firebase.firestore.Timestamp
    ) => {
      const db = firebase.firestore();
      setIsLoadingRemove(true);
      setMessage('');
      db.collection('Users')
        .doc(props.docID)
        .update({
          privilegePeriod: firebase.firestore.FieldValue.arrayRemove({
            start: start,
            end: end,
          }),
        })
        .then(() => {
          db.collection('Users')
            .doc(props.docID)
            .get()
            .then((doc) => {
              setUserPrivilegePeriod(doc.data()?.privilegePeriod);
              setIsLoadingRemove(false);
            })
            .catch((error) => {
              console.error(error);
              setIsLoadingRemove(false);
              setMessage('データ更新時にエラーが発生しました。');
            });
        })
        .catch((error) => {
          console.error(error);
          setIsLoadingRemove(false);
          setMessage('データ削除時にエラーが発生しました。');
        });
    },
    [props.docID]
  );

  const updatePrivilege = useCallback(
    (
      start: firebase.firestore.Timestamp,
      end: firebase.firestore.Timestamp
    ) => {
      const db = firebase.firestore();
      setIsLoadingUpdate(true);
      setMessage('');
      db.collection('Users')
        .doc(props.docID)
        .update({
          privilegePeriod: firebase.firestore.FieldValue.arrayUnion({
            start: start,
            end: end,
          }),
        })
        .then(() => {
          db.collection('Users')
            .doc(props.docID)
            .get()
            .then((doc) => {
              setUserPrivilegePeriod(doc.data()?.privilegePeriod);
              setIsLoadingUpdate(false);
            })
            .catch((error) => {
              console.error(error);
              setIsLoadingUpdate(false);
              setMessage('データ更新時にエラーが発生しました。');
            });
        })
        .catch(() => {
          setIsLoadingUpdate(false);
          setMessage('データ保存時にエラーが発生しました。');
        });
    },
    [props.docID]
  );

  const hasPrivilege = useMemo(() => {
    return userPrivilegePeriod.length === 0
      ? false
      : userPrivilegePeriod
          .map((u) => {
            const endDate = u.end.toDate();
            endDate.setDate(u.end.toDate().getDate() + 1);
            return u.start.toDate() <= new Date() && endDate >= new Date();
          })
          .some((u) => u);
  }, [userPrivilegePeriod]);

  return (
    <div>
      <Right>
        <button
          onClick={() => {
            props.updateUserList();
          }}
        >
          名簿検索に戻る
        </button>
      </Right>
      <Info>
        <H3>
          {props.user.sei} {props.user.mei}さん(特権期間編集)
        </H3>
        <Message>{message}</Message>
        <H4>特権: {hasPrivilege ? '有効' : '無効'}</H4>
        <H4> 新規登録</H4>
        <Hr />
        {/* 新しく支払い情報を登録するテキストボックス */}
        <NamesPrivilegeAdd
          updatePrivilege={updatePrivilege}
          isLoadingUpdate={isLoadingUpdate}
        />
        <H4> 特権期間履歴</H4>
        <Hr />
        <Table>
          <tbody>
            {userPrivilegePeriodSorted.map(({ start, end }, i) => (
              <NamesPrivilegeInfo
                key={i}
                start={start}
                end={end}
                deletePrivilege={deletePrivilege}
              />
            ))}
          </tbody>
        </Table>
        <Right>{isLoadingRemove ? ' 通信中...' : undefined}</Right>
      </Info>
      <Right>
        <button
          onClick={() => {
            props.updateUserList();
          }}
        >
          名簿検索に戻る
        </button>
      </Right>
    </div>
  );
});
